const toNumber = (value) => {
  const numeric = Number(value)
  return Number.isFinite(numeric) ? numeric : 0
}

export const maxZero = (value) => Math.max(0, toNumber(value))

export const normalizeRate = (value, fallback = 0) => {
  const numeric = toNumber(value)
  if (!numeric) return fallback
  // Values stored as percentages (e.g. 19.02 for 19.02%, or 1 for 1%) are
  // divided by 100.  The threshold is >= 1: a vacancy/expense rate of exactly
  // 1.0 means 1%, not 100% — the previous > 1 check treated "1" as 100%.
  return Math.abs(numeric) >= 1 ? numeric / 100 : numeric
}

export function calculateSdaIncomeAndNoi(input = {}) {
  const units = Math.max(0, Math.round(toNumber(input.units)))
  const monthlyGrossRent = toNumber(input.monthlyGrossRent ?? input.grossRentMonthly ?? input.grossRent)
  const grossPotentialRentAnnual = maxZero(
    input.grossPotentialRentAnnual ?? input.grossPotentialRent ?? monthlyGrossRent * 12,
  )
  const otherIncomeAnnual = toNumber(
    input.otherIncomeAnnual ?? (toNumber(input.otherIncomeMonthly ?? input.otherIncome) * 12),
  )
  const vacancyRate = normalizeRate(input.vacancyRate, 0)
  const concessionsRate = normalizeRate(input.concessionsLossToLeaseBadDebtRate, 0)
  const vacancyLoss = input.vacancyLoss != null
    ? -Math.abs(toNumber(input.vacancyLoss))
    : -(grossPotentialRentAnnual * vacancyRate)
  const concessionsLossToLeaseBadDebt = input.concessionsLossToLeaseBadDebt != null
    ? -Math.abs(toNumber(input.concessionsLossToLeaseBadDebt))
    : -(grossPotentialRentAnnual * concessionsRate)
  const effectiveRentalIncome = grossPotentialRentAnnual + vacancyLoss + concessionsLossToLeaseBadDebt
  const effectiveGrossIncome = effectiveRentalIncome + otherIncomeAnnual
  const parsedNoi = maxZero(input.parsedNoi ?? input.noi)
  const explicitExpenses = maxZero(
    input.operatingExpensesAnnual
      ?? input.totalOperatingExpenses
      ?? input.opExpenses
      ?? input.expenses?.totalOperatingExpenses
      ?? input.expenses?.totalExpenses,
  )
  const derivedExpenses = parsedNoi > 0 && explicitExpenses === 0 && effectiveGrossIncome > parsedNoi
    ? effectiveGrossIncome - parsedNoi
    : 0
  const totalOperatingExpenses = explicitExpenses || derivedExpenses
  const rawNoi = effectiveGrossIncome - totalOperatingExpenses
  const shouldTrustParsedNoi = parsedNoi > 0 && input.trustParsedNoi === true
  const noi = shouldTrustParsedNoi
    ? parsedNoi
    : grossPotentialRentAnnual || otherIncomeAnnual || totalOperatingExpenses
      ? maxZero(rawNoi)
      : parsedNoi

  return {
    units,
    grossPotentialRentAnnual,
    otherIncomeAnnual,
    vacancyRate,
    vacancyLoss,
    concessionsLossToLeaseBadDebt,
    effectiveRentalIncome,
    effectiveGrossIncome,
    totalRevenue: grossPotentialRentAnnual + otherIncomeAnnual,
    totalOperatingExpenses,
    noi,
    rawNoi,
    averageMonthlyRent: units ? grossPotentialRentAnnual / 12 / units : 0,
  }
}
